import { HStack, Text } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import type { TimeTrackingEntryFragment } from "@src/__generated__/urql-graphql";
import { TimeTrackingLabel } from "@src/components/modules/time-tracking/TimeSheet/components/Entries/TimeTrackingLabel";
import { timeToNumber } from "@src/components/ui-kit/TimeInput/utils";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import Duration from "./Duration";
import { TimeTrackingItemMenuActions } from "./TimeTrackingItemMenuActions";

interface EntryProps {
  value: TimeTrackingEntryFragment;
  running?: boolean;
}

const Entry = ({ value: e, running }: EntryProps) => {
  const { timeTrackingPopoverStore: store, timeTrackingStore } = useStore();

  return (
    <HStack justify="end">
      {e.task.ourWorkBudgetItem && (
        <TimeTrackingLabel
          py="3"
          mr="auto"
          project={e.task.ourWorkBudgetItem.project}
          brandName={e.task.ourWorkBudgetItem.project.brand.name}
          budgetItemTitle={e.task.ourWorkBudgetItem.title}
          taskName={e.task.name}
          taskId={e.task.id}
          workTypeTitle={e.timeTrackingWorkType.title}
        />
      )}
      {e.specific_time_from && e.specific_time_to ? (
        <>
          <Duration
            value={timeToNumber(e.specific_time_from)}
            highlighted={running}
            billable={e.billable}
          />
          <Text fontSize="2xl">
            <Trans>-</Trans>
          </Text>
          <Duration
            value={timeToNumber(e.specific_time_to)}
            highlighted={running}
            billable
          />
        </>
      ) : (
        <Duration
          value={e.tracked_time_with_running_timer}
          highlighted={running}
          billable={e.billable}
        />
      )}
      {!running && (
        <TimeTrackingItemMenuActions
          value={e}
          onStart={(data) => {
            if (!data?.startTimer) return;
            store.refetchEntries();
            store.togglePopover();
            store.form.reset();
            timeTrackingStore.refetch?.();
          }}
          onDelete={() => {
            store.refetchEntries();
          }}
          onEdit={() => {
            const { form } = store;
            store.popoverRef.current?.scrollTo({
              top: 0,
              behavior: "smooth",
            });
            form.reset();
            form.id.onChange(e.id);
            form.is_locked = e.is_locked ?? false;
            form.notes.onChange(e.note ?? "");
            form.project_id.onChange(e.project.id);
            form.task_id.onChange(e.task.id);
            form.time_tracking_work_type_id.onChange(e.timeTrackingWorkType.id);
            form.time.onChange(e.tracked_time);

            if (e.specific_time_from || e.specific_time_to) {
              form.specificTimeEnabled.onChange(true);
            }
            form.specific_time_from.onChange(
              e.specific_time_from
                ? timeToNumber(e.specific_time_from)
                : undefined,
            );
            form.specific_time_to.onChange(
              e.specific_time_to ? timeToNumber(e.specific_time_to) : undefined,
            );

            form.tracked_for_date.onChange(new Date(e.tracked_for_date));
            form.billable.onChange(e.billable);
            if (!e.task.ourWorkBudgetItem) return;
            form.budget_item_id.onChange(e.task.ourWorkBudgetItem.id);
          }}
        />
      )}
    </HStack>
  );
};

export default observer(Entry);
